import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { lookupSettlement, rememberConciliation, rememberSettlement, rememberTribunalCase } from '../../lib/caseLinks';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { Conciliation, ConciliationMeeting, MeetingStatus, Settlement, TribunalCase } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function ConciliationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { persona } = useAuth();
  const cq = useApiQuery<Conciliation>(`/api/v1/m16/conciliation/${id}`);
  const isOfficer = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER);
  const isMediator = hasAnyRole(persona?.roles, M16_ROLES.MEDIATOR);
  const canDrive = isOfficer || isMediator;

  useEffect(() => {
    if (cq.data) rememberConciliation(cq.data.grievanceId, cq.data.id);
  }, [cq.data]);

  if (cq.loading) return <Spinner label="Loading conciliation…" />;
  if (cq.error) return <ErrorNote message={cq.error} />;
  if (!cq.data) return <ErrorNote message="Not found" />;
  const c = cq.data;
  const meetings = c.meetings ?? [];
  const linkedSettlementId = lookupSettlement(c.id);

  return (
    <div>
      <PageHeader
        title={c.grievance ? `Conciliation — ${c.grievance.refNo}` : `Conciliation ${c.id}`}
        subtitle={`Mediator ${c.mediatorId} · started ${formatDate(c.startedAt)} · window closes ${formatDate(c.deadlineAt)}`}
        actions={<StatusBadge status={c.outcome} />}
      />

      <div className="mb-6 grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Grievance</h2>
          {c.grievance ? (
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-xs font-semibold uppercase text-slate-400">Type</dt>
                <dd className="text-slate-800">{c.grievance.type}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase text-slate-400">Employer</dt>
                <dd className="font-mono text-slate-800">{c.grievance.employerEntityId}</dd>
              </div>
              <div className="col-span-2">
                <Link className="text-xs font-semibold text-brand-600 hover:underline" to={`/grievances/${c.grievance.id}`}>
                  View full grievance →
                </Link>
              </div>
            </dl>
          ) : (
            <p className="text-xs text-slate-400">Grievance details unavailable.</p>
          )}

          {c.failureReason ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold uppercase text-slate-400">Failure reason</div>
              <p className="mt-1 text-sm text-slate-700">{c.failureReason}</p>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          {linkedSettlementId ? (
            <div className="card p-4">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Settlement</h2>
              <Link to={`/settlements/${linkedSettlementId}`} className="btn-primary w-full">
                View settlement
              </Link>
            </div>
          ) : c.outcome === 'PENDING' && canDrive ? (
            <DraftSettlementCard
              conciliationId={c.id}
              onDrafted={(settlementId) => {
                rememberSettlement(c.id, settlementId);
                navigate(`/settlements/${settlementId}`);
              }}
            />
          ) : null}

          {c.outcome === 'PENDING' && canDrive ? (
            <CertifyTribunalCard
              conciliationId={c.id}
              onCertified={(tribunalCase) => {
                rememberTribunalCase(tribunalCase.grievanceId, tribunalCase.id);
                navigate(`/tribunal/${tribunalCase.id}`);
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">
            Sessions ({meetings.length}/{c.maxSessions})
          </h2>
        </div>
        {meetings.length === 0 ? (
          <p className="mb-3 text-xs text-slate-400">No sessions scheduled yet.</p>
        ) : (
          <ul className="mb-4 space-y-2">
            {meetings.map((m) => (
              <MeetingRow key={m.id} meeting={m} canDrive={isMediator} onChanged={cq.reload} />
            ))}
          </ul>
        )}
        {isMediator && c.outcome === 'PENDING' && meetings.length < c.maxSessions ? (
          <ScheduleMeetingForm conciliationId={c.id} deadlineAt={c.deadlineAt} onScheduled={cq.reload} />
        ) : null}
      </div>
    </div>
  );
}

function MeetingRow({ meeting, canDrive, onChanged }: { meeting: ConciliationMeeting; canDrive: boolean; onChanged: () => void }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<MeetingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const recordOutcome = async (status: Extract<MeetingStatus, 'HELD' | 'ADJOURNED' | 'CANCELLED'>) => {
    setBusy(status);
    setError(null);
    try {
      await api.post(`/api/v1/m16/conciliation/meetings/${meeting.id}/outcome`, { status, notes: notes.trim() || undefined });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record outcome');
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="rounded-lg border border-slate-100 bg-slate-50 px-3.5 py-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-700">Session {meeting.sessionNumber}</span>
        <StatusBadge status={meeting.status} />
      </div>
      <div className="mt-0.5 text-xs text-slate-500">Scheduled {formatDateTime(meeting.scheduledAt)}</div>
      {meeting.notes ? <p className="mt-1.5 text-xs text-slate-600">{meeting.notes}</p> : null}
      {canDrive && meeting.status === 'SCHEDULED' ? (
        open ? (
          <div className="mt-2 space-y-2">
            {error ? <ErrorNote message={error} /> : null}
            <textarea className="field-input min-h-[3rem]" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="flex flex-wrap gap-1.5">
              <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => recordOutcome('HELD')} disabled={busy !== null}>
                {busy === 'HELD' ? 'Saving…' : 'Held'}
              </button>
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => recordOutcome('ADJOURNED')} disabled={busy !== null}>
                {busy === 'ADJOURNED' ? 'Saving…' : 'Adjourned'}
              </button>
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => recordOutcome('CANCELLED')} disabled={busy !== null}>
                {busy === 'CANCELLED' ? 'Saving…' : 'Cancelled'}
              </button>
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-secondary mt-2 !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
            Record outcome
          </button>
        )
      ) : null}
    </li>
  );
}

function ScheduleMeetingForm({ conciliationId, deadlineAt, onScheduled }: { conciliationId: string; deadlineAt: string; onScheduled: () => void }) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const schedule = async () => {
    if (!scheduledAt) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/conciliation/${conciliationId}/meetings`, { scheduledAt: new Date(scheduledAt).toISOString() });
      setScheduledAt('');
      onScheduled();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not schedule session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-3">
      <h3 className="mb-2 text-xs font-bold uppercase text-slate-500">Schedule a session</h3>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Date &amp; time" hint={`Must be on/before ${formatDate(deadlineAt)}`} className="!mb-0">
          <input type="datetime-local" className="field-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
        <button className="btn-primary" onClick={schedule} disabled={busy || !scheduledAt}>
          {busy ? 'Scheduling…' : 'Schedule'}
        </button>
      </div>
    </div>
  );
}

function DraftSettlementCard({ conciliationId, onDrafted }: { conciliationId: string; onDrafted: (settlementId: string) => void }) {
  const [termsText, setTermsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = async () => {
    if (termsText.trim().length < 10) return;
    setBusy(true);
    setError(null);
    try {
      const settlement = await api.post<Settlement>(`/api/v1/m16/conciliation/${conciliationId}/settlement`, { termsText: termsText.trim() });
      onDrafted(settlement.id);
    } catch (e) {
      // A repeat draft attempt 409s with the existing settlement's id in
      // `details` — the only way to discover it if this browser's cache
      // doesn't already have it (see lib/caseLinks.ts).
      if (e instanceof ApiError && e.code === 'CONFLICT' && e.details && typeof e.details === 'object' && 'settlementId' in e.details) {
        onDrafted(String((e.details as { settlementId: unknown }).settlementId));
        return;
      }
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not draft settlement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Draft settlement</h2>
      <p className="mb-2 text-xs text-slate-500">If the parties reached agreement — both sides then e-sign it.</p>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea
        className="field-input mb-2 min-h-[5rem]"
        placeholder="Settlement terms (at least 10 characters)…"
        value={termsText}
        onChange={(e) => setTermsText(e.target.value)}
      />
      <button className="btn-primary w-full" onClick={draft} disabled={busy || termsText.trim().length < 10}>
        {busy ? 'Drafting…' : 'Draft settlement'}
      </button>
    </div>
  );
}

function CertifyTribunalCard({ conciliationId, onCertified }: { conciliationId: string; onCertified: (tribunalCase: TribunalCase) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const certify = async () => {
    if (reason.trim().length < 5) return;
    setBusy(true);
    setError(null);
    try {
      const tribunalCase = await api.post<TribunalCase>(`/api/v1/m16/conciliation/${conciliationId}/certify-tribunal`, { reason: reason.trim() });
      onCertified(tribunalCase);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not certify to tribunal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Certify to tribunal</h2>
      <p className="mb-2 text-xs text-slate-500">If conciliation failed — files an e-bundle to the employment tribunal (M37 e-filing seam).</p>
      {!open ? (
        <button className="btn-danger w-full" onClick={() => setOpen(true)}>
          Certify to tribunal…
        </button>
      ) : (
        <>
          {error ? (
            <div className="mb-2">
              <ErrorNote message={error} />
            </div>
          ) : null}
          <textarea
            className="field-input mb-2 min-h-[4rem]"
            placeholder="Reason (at least 5 characters)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button className="btn-danger flex-1" onClick={certify} disabled={busy || reason.trim().length < 5}>
              {busy ? 'Certifying…' : 'Confirm certification'}
            </button>
            <button className="btn-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
      <InfoNote message="m16 tracks transmission to M37 honestly: no live service-to-service intake contract exists yet, so the resulting tribunal case starts PENDING_TRANSMISSION until that seam is wired." />
    </div>
  );
}
